import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import dayjs from 'dayjs';
import { LabelCaps } from '../LabelCaps';
import { featureFlags } from '../../../utils/featureFlags';
import type { BillingPeriod } from '../../../utils/paymentTiers';

const REASONS = [
  'Too expensive',
  'Missing features I need',
  'Not using it enough',
  'Switching to another tool',
  'Other',
];

interface CancelSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * ``keepPublicVisible`` (GH#1254): the step-3 "keep your public work
   * visible after expiry?" answer. Only passed when the
   * ``enableExpiryLifecycle`` flag is on; undefined otherwise.
   */
  onConfirm: (reason?: string, keepPublicVisible?: boolean) => Promise<void>;
  onSwitchInstead: () => void;
  renewDate: string;
  currentBillingPeriod: BillingPeriod;
}

export const CancelSubscriptionDialog: React.FC<CancelSubscriptionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  onSwitchInstead,
  renewDate,
  currentBillingPeriod,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [reason, setReason] = useState<string>('');
  const [keepPublic, setKeepPublic] = useState<'yes' | 'no'>('no');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // GH#1254: the public-visibility step only exists while the expiry
  // lifecycle is live — without it, expiry never happens, so the question
  // would be meaningless.
  const askKeepPublic = featureFlags.isEnabled('enableExpiryLifecycle');

  useEffect(() => {
    if (open) {
      setStep(1);
      setReason('');
      setKeepPublic('no');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const formattedRenew = dayjs(renewDate).format('MMM D, YYYY');

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(reason || undefined, askKeepPublic ? keepPublic === 'yes' : undefined);
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to cancel.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSwitchInstead = () => {
    onSwitchInstead();
    onClose();
  };

  const confirmButton = (
    <Button variant="contained" color="error" onClick={handleConfirm} disabled={submitting}>
      Confirm Cancellation
    </Button>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1.5 }}>
        Cancel Subscription
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>

      {step === 1 && (
        <>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              You&apos;ll keep full access to all your catalogs and objects until <strong>{formattedRenew}</strong>. After that date, your account will revert to the Viewer plan — your data stays, but editing is disabled.
            </Typography>
            {currentBillingPeriod === 'monthly' && (
              <Alert
                icon={<InfoOutlinedIcon />}
                severity="warning"
                sx={{ mt: 2.25 }}
              >
                <strong>Before you go:</strong> switching to yearly saves you 2 months per catalog. Your current plan costs $15/month — yearly is $120 ($10/month).
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleSwitchInstead}
              sx={{ visibility: currentBillingPeriod === 'monthly' ? 'visible' : 'hidden' }}
            >
              Switch to Yearly Instead
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={onClose}>Keep Subscription</Button>
              <Button variant="contained" color="error" onClick={() => setStep(2)}>
                Continue
              </Button>
            </Box>
          </DialogActions>
        </>
      )}

      {step === 2 && (
        <>
          <DialogContent dividers>
            <LabelCaps>Why are you cancelling? (Optional)</LabelCaps>
            <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mt: 1.25 }}>
              {REASONS.map((r) => (
                <FormControlLabel key={r} value={r} control={<Radio />} label={r} />
              ))}
            </RadioGroup>
            <Box
              sx={{
                mt: 2.5,
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderRadius: 1,
              }}
            >
              <Typography variant="body2">
                <strong>Confirming cancellation.</strong> Access continues through{' '}
                <strong>{formattedRenew}</strong>. No further charges after that date.
              </Typography>
            </Box>
            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
                {error}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStep(1)} disabled={submitting}>Back</Button>
            {askKeepPublic ? (
              <Button variant="contained" color="error" onClick={() => setStep(3)}>
                Continue
              </Button>
            ) : (
              confirmButton
            )}
          </DialogActions>
        </>
      )}

      {step === 3 && (
        <>
          <DialogContent dividers data-testid="cancel-keep-public-step">
            <LabelCaps>Your public work</LabelCaps>
            <Typography variant="body2" sx={{ mt: 1.25, lineHeight: 1.6 }}>
              Some of your objects may be publicly visible. When your subscription ends,
              public visibility normally pauses along with the catalog. Would you like your
              public work to <strong>stay visible</strong> after your subscription ends?
            </Typography>
            <RadioGroup
              value={keepPublic}
              onChange={(e) => setKeepPublic(e.target.value as 'yes' | 'no')}
              sx={{ mt: 1.25 }}
            >
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label="Yes — keep my public objects visible"
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label="No — pause public visibility with the catalog"
              />
            </RadioGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              You can change this later by contacting support. Nothing is ever deleted either way.
            </Typography>
            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
                {error}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStep(2)} disabled={submitting}>Back</Button>
            {confirmButton}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
